// The five friendly meters players see. DERIVED from physiology, never stored —
// kid-readable surface over a real engine (BIOLOGY-ENGINE.md §1).

import { clamp, round2 } from '../core/math';
import type { ChickenEntity } from '../core/types';

export interface VisibleMeters {
  hunger: number;
  thirst: number;
  hygiene: number;
  health: number;
  happiness: number;
}

const SEVERITY_WEIGHT = { mild: 6, moderate: 15, severe: 30 } as const;

export function deriveMeters(chicken: ChickenEntity): VisibleMeters {
  const p = chicken.physiology;

  // Meters read "how full", so they track the underlying reserve directly.
  // Clamped like every other field below: this is the display edge, and a save
  // or dev override carrying an out-of-range reserve must never show a
  // negative percentage to the player.
  const hunger = clamp(p.energyReserve, 0, 100);
  const thirst = clamp(p.hydration, 0, 100);

  // Hygiene is what an inspection would show: skin/gut/parasite load.
  const parasites = chicken.conditions.some(
    (c) => c.id === 'scaly_leg_mites' || c.id === 'bumblefoot' || c.id === 'pasting_up',
  );
  const hygiene = clamp(p.gutHealth * 0.5 + (parasites ? 20 : 100) * 0.5, 0, 100);

  // Health folds in every active condition, weighted by severity.
  const illnessLoad = chicken.conditions.reduce(
    (sum, c) => sum + SEVERITY_WEIGHT[c.severity],
    0,
  );
  const health = clamp(
    p.immune * 0.35 + p.bodyCondition * 0.35 + p.gutHealth * 0.3 - illnessLoad,
    0,
    100,
  );

  const scared = chicken.conditions.some((c) => c.id === 'scared');
  const happiness = clamp(
    100 - p.stress - (scared ? 20 : 0) - (chicken.currentState === 'ESCAPED' ? 25 : 0),
    0,
    100,
  );

  return {
    hunger: round2(hunger),
    thirst: round2(thirst),
    hygiene: round2(hygiene),
    health: round2(health),
    happiness: round2(happiness),
  };
}

export type Mood = 'happy' | 'content' | 'uncomfortable' | 'miserable';

/** Single-glance mood for the flock list and the bird's mood emoji. */
export function moodOf(chicken: ChickenEntity): Mood {
  const m = deriveMeters(chicken);
  const worst = Math.min(m.health, m.happiness, m.hunger, m.thirst);
  if (worst > 70) return 'happy';
  if (worst > 45) return 'content';
  if (worst > 20) return 'uncomfortable';
  return 'miserable';
}
